/* eslint-disable camelcase */
import { useState } from 'react'

import styles from '@/pages/app/BillingOps/styles.module.scss'

import { isApiError } from '@/api'
import { commitBillingOp, listCostCentersForEnterprise, previewBillingOp } from '@/api/billingOps'
import { BulkLineError, BulkRow, bulkRowsToCsv, parseBulkLines, rowsToCommit } from '@/pages/app/BillingOps/bulkConvert'
import { BillingOpsPreviewView, OPERATION_KINDS } from '@/pages/app/BillingOps/types'

const errorMessage = (error: unknown, fallback: string): string =>
  isApiError(error) ? error.detail || error.message : fallback

const BulkConvertToSalesOwned = () => {
  const [text, setText] = useState('')
  const [rows, setRows] = useState<BulkRow[]>([])
  const [parseErrors, setParseErrors] = useState<BulkLineError[]>([])
  const [running, setRunning] = useState<'preview' | 'commit' | null>(null)
  const [copyState, setCopyState] = useState<'idle' | 'copied' | 'failed'>('idle')

  const updateRow = (lineNumber: number, patch: Partial<BulkRow>) =>
    setRows((current) => current.map((row) => (row.lineNumber === lineNumber ? { ...row, ...patch } : row)))

  const handlePreviewAll = async () => {
    const parsed = parseBulkLines(text)
    setParseErrors(parsed.errors)
    setCopyState('idle')
    const initial: BulkRow[] = parsed.lines.map((line) => ({
      ...line,
      status: 'pending',
      costCenterId: null,
      preview: null,
      error: null,
      idempotencyRoot: crypto.randomUUID(),
    }))
    setRows(initial)
    if (initial.length === 0) return
    setRunning('preview')
    // Sequential on purpose: a one-time migration of a few hundred orgs doesn't need concurrency.
    for (const row of initial) {
      try {
        const costCenters = (await listCostCentersForEnterprise(row.enterpriseId)).data.cost_centers
        const general = costCenters.find((cc) => cc.is_general)
        if (!general) {
          updateRow(row.lineNumber, { status: 'failed', error: 'No General cost center found' })
          continue
        }
        const response = await previewBillingOp({
          cost_center_id: general.id,
          operation_kind: OPERATION_KINDS.CONVERT_TO_SALES_OWNED,
          input: row.input as unknown as object,
        })
        updateRow(row.lineNumber, {
          status: 'previewed',
          costCenterId: general.id,
          preview: response.data.preview as unknown as BillingOpsPreviewView,
        })
      } catch (error) {
        updateRow(row.lineNumber, { status: 'failed', error: errorMessage(error, 'Preview failed') })
      }
    }
    setRunning(null)
  }

  const handleCommitAll = async () => {
    const toCommit = rowsToCommit(rows)
    if (toCommit.length === 0) return
    const confirmed = window.confirm(
      `Convert ${toCommit.length} organization${toCommit.length === 1 ? '' : 's'} to sales-owned billing? ` +
        'This is IRREVERSIBLE from this page.'
    )
    if (!confirmed) return
    setRunning('commit')
    for (const row of toCommit) {
      try {
        await commitBillingOp({
          cost_center_id: row.costCenterId as string,
          operation_kind: OPERATION_KINDS.CONVERT_TO_SALES_OWNED,
          input: row.input as unknown as object,
          idempotency_root: row.idempotencyRoot,
        })
        updateRow(row.lineNumber, { status: 'committed', error: null })
      } catch (error) {
        updateRow(row.lineNumber, { status: 'failed', error: errorMessage(error, 'Commit failed') })
      }
    }
    setRunning(null)
  }

  const handleCopyCsv = async () => {
    try {
      await navigator.clipboard.writeText(bulkRowsToCsv(rows))
      setCopyState('copied')
    } catch {
      setCopyState('failed')
    }
  }

  const previewedCount = rows.filter((row) => row.status === 'previewed').length

  return (
    <div className={styles.panel}>
      <div className={styles.section}>
        <h2 className={styles.sectionTitle}>Bulk: convert to sales-owned</h2>
        <p className={styles.helperText}>
          One organization per line: <code>enterprise_id, seats, seat_cap</code> (seat cap optional). Each line previews
          and commits the same single-org operation against the enterprise&apos;s General cost center; a failed line
          doesn&apos;t stop the rest, and re-running is safe. Lines with errors are skipped. Copy the results CSV before
          editing the list: a converted org no longer shows its old Stripe ids, so Support&apos;s list can&apos;t be
          rebuilt later.
        </p>
        <textarea
          className={styles.input}
          rows={8}
          value={text}
          onChange={(e) => {
            setText(e.target.value)
            setRows([])
            setParseErrors([])
          }}
          placeholder="00000000-0000-0000-0000-000000000000, 40, 50"
          disabled={running !== null}
          aria-label="Organizations to convert"
        />
        {parseErrors.length > 0 && (
          <div className={styles.errorBanner}>
            <ul className={styles.list}>
              {parseErrors.map((e) => (
                <li key={e.lineNumber}>
                  Line {e.lineNumber}: {e.message}
                </li>
              ))}
            </ul>
          </div>
        )}
        <div className={styles.actionRow}>
          <button
            type="button"
            className={styles.opItem}
            style={{ width: 'auto' }}
            onClick={handlePreviewAll}
            disabled={running !== null || text.trim() === ''}
          >
            <span className={styles.opItemKind}>{running === 'preview' ? 'Previewing…' : 'Preview all'}</span>
          </button>
          <button
            type="button"
            className={styles.opItem}
            style={{ width: 'auto' }}
            onClick={handleCommitAll}
            disabled={running !== null || previewedCount === 0}
          >
            <span className={styles.opItemKind}>
              {running === 'commit' ? 'Committing…' : `Commit ${previewedCount} previewed`}
            </span>
          </button>
          <button
            type="button"
            className={styles.opItem}
            style={{ width: 'auto' }}
            onClick={handleCopyCsv}
            disabled={running !== null || rows.length === 0}
          >
            <span className={styles.opItemKind}>
              {copyState === 'copied'
                ? 'Copied'
                : copyState === 'failed'
                  ? 'Copy failed, try again'
                  : 'Copy results as CSV'}
            </span>
          </button>
        </div>
      </div>

      {rows.length > 0 && (
        <div className={styles.section}>
          <table className={styles.diffTable}>
            <caption className={styles.helperText}>Bulk conversion results</caption>
            <thead>
              <tr>
                <th>Line</th>
                <th>Enterprise</th>
                <th>Status</th>
                <th>Details</th>
                <th>Stripe objects for Support</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.lineNumber}>
                  <td>{row.lineNumber}</td>
                  <td>{row.enterpriseId}</td>
                  <td>{row.status}</td>
                  <td>{row.error ?? row.preview?.customer_impact.summary ?? ''}</td>
                  <td>
                    {(row.preview?.customer_impact.artifacts ?? [])
                      .filter((a) => a.stripe_id)
                      .map((a) => `${a.kind}: ${a.stripe_id}`)
                      .join(', ')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

export default BulkConvertToSalesOwned
